/**
 * Announces the new page after a client-side navigation.
 *
 * A document load is announced by the screen reader itself. A route change is not — the URL and
 * the content change with no event the assistive technology recognises, so without this a
 * keyboard user activates a link and hears nothing at all.
 *
 * The first title is skipped deliberately: on arrival the browser has already said where we are,
 * and repeating it is noise.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { VisuallyHidden } from '@catalog/ui';

import { getPageTitle, subscribePageTitle } from '../lib/page-title.js';

export function RouteAnnouncer() {
  const title = useSyncExternalStore(subscribePageTitle, getPageTitle, () => '');
  const [announcement, setAnnouncement] = useState('');
  const isFirst = useRef(true);

  useEffect(() => {
    if (title.length === 0) return;

    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    setAnnouncement(title);
  }, [title]);

  return (
    <VisuallyHidden>
      {/*
        `role="status"` rather than `aria-live="assertive"`: arriving on a page is not an
        interruption, and an assertive region cuts off whatever the reader was in the middle of.
      */}
      <div role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
    </VisuallyHidden>
  );
}
